/* global G */
import { bulk, curry, deleteKey, setKey } from 'lib/util'
import validate from 'lib/sequence/model/validate'
import { get } from 'lib/sequence/component/state/value'
import { hide, show } from 'lib/sequence/component/state/hidden'
import { actionWithHooks } from 'lib/sequence/module/action'
import reset from 'lib/sequence/model/api/reset'
import sequenceComponentFind from 'lib/sequence/component/children/find'
import setStepTab, { RESULT } from 'app/_shared/events/setStepTab'
import findDevices from 'app/_shared/events/device/find'
import setItem from 'app/serviceItem/event/setItem'

const bulkHide = bulk(hide)

/**
 * Validates the serial field and uses {@link findDevices} to attempt to find a product, while
 * handling the different possibilities:
 *  * If a device is found it means that the product we need is attached to it, so we show a
 * warning
 *  * If no product is found, we show an info message
 *  * If an available product is found, we go to the next step
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {Gaia.Component.Spec} component  the current action's main component
 * @param {Gaia.PlatformEvent} event    the event object that triggered this handler
 * @returns {Promise<void>} void
 */
const searchProduct = async (module, component, event) => {
  const model = module[G.MODEL]
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]

  const {
    form,
    serial,
    btnMore,
    btnNoSerial,
    existingHelp,
    resultsList,
  } = sequenceComponentFind(actionComponent)

  // hiding help buttons
  bulkHide(btnNoSerial, existingHelp, btnMore)
  // clearing currently stored data
  reset(model, { shallow: true })
  // clearing model's error state
  setKey(null, G.ERROR, model[G.STATE])
  // clearing model's bulk flag
  deleteKey(G.BULK, model[G.STATE])
  // validating model against form
  await validate(model)(form)

  if (!model[G.STATE][G.ERROR]) {
    const serialValue = get(serial)
    const devices = await findDevices(module, serial, serialValue)

    devices?.length > 1
      ? setKey({ term: serialValue }, G.META, resultsList[G.STATE])
        && await setStepTab(RESULT)(module, component, event)
      : await setItem(module, component, devices?.[0])
  } else {
    show(btnNoSerial)
  }

  await actionWithHooks(module[G.STATE][G.ACTION])([])
}

export default curry(searchProduct)
